import './header-group.scss';

import { z } from 'zod';

import { cn } from '@sola/ark-core/utils/helpers.js';
import { IconNamesSafe } from '@sola/schema/lib/icons/icon-names.js';
import { getHeaderDisplayLevel } from '@sola/utils/assembly.js';

import { Image } from '../../atoms/image/image';
import { getBaseSizeProps } from '../../utils/text-utils';
import { FeatureBase, FeatureBaseProps } from '../feature-base/feature-base';
import { FeatureButtons } from '../feature-buttons/feature-buttons';

const HeaderGroupProps = FeatureBaseProps.extend({
  displayLevel: z.number().optional(),
  icon: IconNamesSafe.optional(),
  imagePlacement: z.enum(['left', 'right']).optional(),
  variant: z.enum(['standard', 'horizontal']).optional(),
  inline: z.boolean().optional(),
});
export type HeaderGroupProps = z.infer<typeof HeaderGroupProps>;

export const HeaderGroup: React.FC<HeaderGroupProps> = ({
  seoLevel = 2,
  displayLevel,
  media,
  mediaLogo,
  tertiaryCTA,
  ...props
}) => {
  const hasSideLink = !media?.image && tertiaryCTA;
  const hasBaseButtons = props.primaryCTA || props.secondaryCTA;
  const headerDisplayLevel = getHeaderDisplayLevel(seoLevel);

  const featureBaseClassName = [];
  if (hasSideLink) {
    featureBaseClassName.push('has-sidelink');

    if (!hasBaseButtons) {
      featureBaseClassName.push('has-sidelink--tertiary');
    }
  }

  return (
    <div
      className={cn(
        'HeaderGroup flex flex-col justify-between md:flex-row',
        media?.image ? 'items-center' : 'items-start',
        hasSideLink ? 'gap-comfy-sm md:gap-comfy-xl' : 'gap-comfy-xl',
      )}
    >
      <FeatureBase
        className={cn(
          'HeaderGroup__content w-full max-w-md',
          featureBaseClassName.join(' '),
        )}
        {...props}
        {...getBaseSizeProps(displayLevel || headerDisplayLevel)}
        media={mediaLogo}
        seoLevel={seoLevel}
        tertiaryCTA={tertiaryCTA}
        variant="standard"
        inline
      />
      {media?.image && (
        <div className="HeaderGroup__image">
          <Image
            image={media?.image}
            aspectRatio={media?.image.ratio || '1'}
            rounded="lg"
            className={cn('HeaderGroup__image-img')}
            priority={seoLevel === 1}
          />
        </div>
      )}
      {hasSideLink && (
        <div className={cn('HeaderGroup__ctas')}>
          <FeatureButtons
            {...{ tertiaryCTA }}
            variant="stacked"
            className="md:flex-col md:items-end gap-comfy-sm"
            isLinks
            inline
          />
        </div>
      )}
    </div>
  );
};
